import { eventCategory } from "@foretagsplatsen/yaem";
import SelectionItem from "./SelectionItem.js";

const eventHandler = eventCategory();

/**
 * I am representing a selection of `SelectionItem'.
 * I mainly provide collection API methods,
 * as well as method to perform selection related behavior.
 *
 * I act as a set based on the item id.
 *
 * I also keep in cache the last -shift- added items
 * for performance issue.
 */
class InstantiateSelection {
	constructor({ items } = {}) {
		this._items = items || [];
		this._currentShiftSelection = [];

		this.onSelectedItemChanged = eventHandler.createEvent(
			"selectedItemChanged",
		);
	}

	entries() {
		return this._items.map((each) => each.entry());
	}

	registerToEventManager(selectionEventsManager) {
		selectionEventsManager.onShiftSelect.register((widget, widgets) => {
			this._shiftSelect(widget, widgets);
		});

		selectionEventsManager.onToggleEntrySelection.register((spec) => {
			this._toggleEntrySelection(spec);
		});

		selectionEventsManager.onToggleSingleEntrySelection.register((spec) => {
			this._toggleSingleEntrySelection(spec);
		});

		selectionEventsManager.onEmptySelection.register(() => {
			this._emptySelection();
		});
	}

	removeAll(postAction) {
		const oldItems = this._items;

		this._items = [];

		if (postAction) {
			postAction(oldItems);
		}

		this._triggerChange();
	}

	lastNonShift() {
		for (let i = this._items.length - 1; i >= 0; i--) {
			const each = this._items[i];
			if (!each.isShiftSelection()) {
				return each;
			}
		}

		return null;
	}

	includesId(id) {
		return this._items.some((each) => each.entry().id() === id);
	}

	includes(item) {
		return this.includesId(item.entry().id());
	}

	includesEntry(entry) {
		return this.includesId(entry.id());
	}

	addAll(itemsToAdd, trigger) {
		trigger = trigger !== false;

		itemsToAdd.forEach((each) => {
			this._add(each, false);
		});

		if (trigger) {
			this._triggerChange();
		}

		return itemsToAdd;
	}

	addToSelection(selection) {
		selection.addAll(this._items);
	}

	addItem(spec) {
		const item = this.createItem(spec);

		if (!this.includes(item)) {
			this._add(item);
			item.setSelected();
		}
	}

	indexOf(lookedUpItem) {
		for (let i = 0; i < this._items.length; i++) {
			const item = this._items[i];
			if (lookedUpItem.entry().id() === item.entry().id()) {
				return i;
			}
		}

		return -1;
	}

	remove(item) {
		const index = this.indexOf(item);
		if (index === -1) {
			return null;
		}

		this._items.splice(index, 1);
		this._triggerChange();

		return item;
	}

	notEmpty() {
		return this._items.length !== 0;
	}

	isEmpty() {
		return this._items.length === 0;
	}

	/**
	 * async forEach
	 * fn takes 2 arguments:
	 * - the element to iterate on
	 * - the callback function
	 */
	forEach(fn, onSuccess) {
		this._recur(Object.create(this._items), fn, onSuccess);
	}

	isSingleSelection() {
		return this._items.length === 1;
	}

	every(callback) {
		return this._items.every(callback);
	}

	some(callback) {
		return this._items.some(callback);
	}

	createItem(spec) {
		return new SelectionItem(spec);
	}

	first() {
		return this._items[0];
	}

	firstEntry() {
		const first = this.first();
		return first ? first.entry() : undefined;
	}

	allShiftSelectionDo(action) {
		const selection = [];

		this._items.forEach((each) => {
			if (!each.isShiftSelection()) {
				return;
			}
			selection.push(each);
		});

		selection.forEach(action);
	}

	numberOfItems() {
		return this._items.length;
	}

	numberOfSubDirectories() {
		let result = 0;

		this._items.forEach((each) => {
			if (!each.entry().isFolder()) {
				return;
			}
			result = result + 1;
		});

		return result;
	}

	numberOfDocuments() {
		let result = 0;

		this._items.forEach((each) => {
			if (!each.entry().isFile()) {
				return;
			}
			result = result + 1;
		});

		return result;
	}

	itemNames() {
		return this._items.map((each) => each.entry().name());
	}

	itemNamesSeparatedBy(separator) {
		return this.itemNames().join(separator || " ");
	}

	updateWidgetForEntry(spec) {
		const entry = spec.entry;
		const widget = spec.widget;

		this._items.forEach((each) => {
			if (each.entry().id() !== entry.id()) {
				return;
			}
			each.setWidget(widget);
		});
	}

	_add(item, trigger) {
		trigger = trigger !== false;

		if (!this.includes(item)) {
			this._checkShiftSelection(item);
			this._items.push(item);
			if (trigger) {
				this._triggerChange();
			}
		}
	}

	_recur(remaining, fn, onSuccess) {
		// Nico & Ben: It took us some time to write it. It should take
		// you some time to understand it too :)
		let head;
		if (remaining.length === 0) {
			onSuccess();
		} else {
			head = remaining[0];
			remaining.splice(0, 1);

			fn(head, () => {
				this._recur(remaining, fn, onSuccess);
			});
		}
	}

	_toggleEntrySelection(spec) {
		const item = this.createItem(spec);

		this._toggleSelection(
			item,
			() => item.setSelected(),
			() => item.unsetSelected(),
		);
	}

	_toggleSingleEntrySelection(spec) {
		const item = this.createItem(spec);

		this._toggleSingleSelection({
			item,
			onAddition: () => item.setSelected(),
			onRemoval: () => item.unsetSelected(),
			postAction: (toRemove) => {
				toRemove.forEach((each) => {
					each.unsetSelected();
				});
			},
		});
	}

	_toggleSingleSelection({ item, onAddition, onRemoval, postAction }) {
		if (this.includes(item)) {
			if (this.isSingleSelection()) {
				this.remove(item);
				onRemoval();
			} else {
				this.removeAll(postAction);
				this._add(item);
				onAddition();
			}
		} else {
			this.removeAll(postAction);
			this._add(item);
			onAddition();
		}
	}

	_toggleSelection(item, onAddition, onRemoval) {
		if (this.includes(item)) {
			this.remove(item);
			onRemoval();
		} else {
			this._add(item);
			onAddition();
		}
	}

	_shiftSelect(widget, widgets) {
		const position = widget.position();
		const lastPosition = this.lastNonShift().position();
		let i;

		this.allShiftSelectionDo(function (each) {
			this.remove(each);
			each.unsetSelected();
		});

		if (position < lastPosition) {
			for (i = position; i < lastPosition; i++) {
				this.addItem({
					entry: widgets[i].model(),
					widget: widgets[i],
					isShiftSelection: true,
				});
			}
		}

		if (lastPosition < position) {
			for (i = lastPosition; i < position; i++) {
				this.addItem({
					entry: widgets[i + 1].model(),
					widget: widgets[i + 1],
					isShiftSelection: true,
				});
			}
		}
	}

	_emptySelection() {
		this.removeAll((toRemove) => {
			toRemove.forEach((each) => {
				each.unsetSelected();
			});
		});
	}

	_checkShiftSelection(item) {
		if (item.isShiftSelection()) {
			this._currentShiftSelection.push(item);
		} else {
			this._currentShiftSelection.forEach((each) => {
				each.setShiftSelection(false);
			});

			this._currentShiftSelection = [];
		}
	}

	_triggerChange() {
		eventHandler.trigger("selectedItemChanged");
	}
}

const selection = new InstantiateSelection();
selection.model = (...rest) => new InstantiateSelection(...rest);

export default selection;
